import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from 'components/@common/CircularProgress';
import UserClassesInformation from 'components/@common/UserProfile/UserClassesInformation';
import PageContainer from 'components/@layout/PageContainer';
import GET_USER_CLASSROOMS_AND_ORGANIZATIONS from 'graph/composite/get-user-classrooms-and-organizations-query';
import useRouteAction from 'hooks/route-action/use-route-action';
import useStore from 'store';
import UserProfileInformation from './components/UserProfileInformation';
import UserLicenseInformation from './components/UserLicenseInformation';
import UserOrganizationRoleInformation, {
  OrganizationListItem
} from './components/UserOrganizationRoleInformation';
import EditProfileModal from './modals/EditProfileModal';
import { ModuleProps } from 'types';
import { AlertVariant } from 'types/enums';
import { User } from 'types/schema-generated-types';
import { proxyInsufficientPrivilegesError } from 'utilities/proxy-apollo-error';
import { filterPublicOrgAssociationRoles } from 'utilities/organization-util';

const FILTER_ORGANIZATIONS = ['SCHOOL', 'USER_GROUP', 'DISTRICT'];

const UserProfile: React.FC<ModuleProps> = () => {
  const classes = useStyles();
  const { t } = useTranslation('UserProfile');
  const [{ user }, { openQuarterModal, closeModal, openAlert }] = useStore();
  const me = user.me as User;

  const { data, loading, error } = useQuery(GET_USER_CLASSROOMS_AND_ORGANIZATIONS, {
    variables: { userId: me.id },
    errorPolicy: 'all'
  });

  const onSubmitSuccess = (): void => {
    closeModal();
    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('Profile Updated'),
      text: t('Your profile has been successfully updated.')
    });
  };

  const handleEditProfile = (): void => {
    openQuarterModal({
      body: <EditProfileModal onClose={closeModal} onSubmitSuccess={onSubmitSuccess} />,
      title: t('Edit Profile Information'),
      routeAction: 'edit-profile'
    });
  };

  useRouteAction(({ action }) => {
    if (!action) return;
    switch (action) {
      case 'edit-profile':
        return handleEditProfile();
      default:
        return null;
    }
  });

  if (loading || !data) return <CircularProgress loading={true} />;

  const classrooms = data.classrooms;
  const licenseUsage = me.licenseUsage ?? [];

  const organizations: OrganizationListItem[] =
    data.user?.organizationAssociations
      ?.filter(({ organization }: any) => FILTER_ORGANIZATIONS.includes(organization.type))
      ?.map(({ organizationRoles, organization }: any) => {
        const { name } = organization;
        const roleNames = filterPublicOrgAssociationRoles(organizationRoles)?.map(
          ({ displayName }: any) => displayName
        );
        return { name, roles: roleNames };
      }) ?? [];

  return (
    <PageContainer loading={loading} error={proxyInsufficientPrivilegesError(error)}>
      <Grid container spacing={4} className={classes.container}>
        <Grid item xs={12}>
          <UserProfileInformation user={me} onEditProfile={handleEditProfile} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <UserLicenseInformation
            licenseUsage={licenseUsage}
            emptyMessage={t('You have no assigned licenses.')}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <UserOrganizationRoleInformation organizations={organizations} />
        </Grid>
        <Grid item xs={12}>
          <UserClassesInformation
            classrooms={classrooms}
            emptyMessage={t('You have no active classes.')}
            btnText={'Go to My Classes & Students'}
          />
        </Grid>
      </Grid>
    </PageContainer>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
  }
}));

export default UserProfile;
